import { useState, useEffect } from 'react';
import { modelsApi, Model } from '../../api/models.api';
import { categoriesApi, Category } from '../../api/categories.api';
import { brandsApi, Brand } from '../../api/brands.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';

const ModelList = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    parentCategory: '',
    brand: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [modelsData, categoriesData, brandsData] = await Promise.all([
        modelsApi.getAll(),
        categoriesApi.getAll(),
        brandsApi.getAll(),
      ]);
      setModels(modelsData);
      setCategories(categoriesData);
      setBrands(brandsData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (model?: Model) => {
    if (model) {
      setEditingModel(model);
      const cat = categories.find(c => c.id === model.category?._id);
      const parentCategoryId = cat?.parentCategory?._id || '';
      
      setFormData({
        name: model.name,
        description: model.description || '',
        category: model.category?._id || '',
        parentCategory: parentCategoryId,
        brand: model.brand?._id || '',
      });
    } else {
      setEditingModel(null);
      setFormData({
        name: '',
        description: '',
        category: '',
        parentCategory: '',
        brand: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleParentCategoryChange = (parentCategoryId: string) => {
    setFormData({ 
      ...formData, 
      parentCategory: parentCategoryId,
      category: '',
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingModel(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form Data:', formData);
    
    if (!formData.category) {
      setError('Lütfen alt kategori seçiniz');
      return;
    }
    
    try {
      const submitData: any = {
        name: formData.name,
        category: formData.category,
      };
      
      if (formData.description) {
        submitData.description = formData.description;
      }
      
      if (formData.brand) {
        submitData.brand = formData.brand;
      }

      console.log('Submit Data:', submitData);

      if (editingModel) {
        await modelsApi.update(editingModel.id, submitData);
      } else {
        await modelsApi.create(submitData);
      }
      await fetchData();
      handleCloseModal();
    } catch (err: any) {
      console.error('Submit Error:', err);
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu modeli silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await modelsApi.delete(id);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  // Get subcategories (categories with a parent)
  const subcategories = categories.filter((cat) => cat.parentCategory);
  
  // Get parent categories (categories without a parent)
  const parentCategories = categories.filter((cat) => !cat.parentCategory);
  
  // Get subcategories for selected parent
  const availableSubcategories = formData.parentCategory 
    ? subcategories.filter(cat => cat.parentCategory?._id === formData.parentCategory)
    : [];

  const columns: Column<Model>[] = [
    { key: 'name', header: 'Model Adı' },
    {
      key: 'category',
      header: 'Kategori',
      render: (item) => item.category?.name || '-',
    },
    {
      key: 'brand',
      header: 'Marka',
      render: (item) => item.brand?.name || '-',
    },
    { key: 'description', header: 'Açıklama', render: (item) => item.description || '-' },
    {
      key: 'isActive',
      header: 'Durum',
      render: (item) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {item.isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Modeller yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Modeller</h1>
        <Button onClick={() => handleOpenModal()}>Yeni Model Ekle</Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <Table data={models} columns={columns} emptyMessage="Henüz model bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingModel ? 'Model Düzenle' : 'Yeni Model Ekle'}
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Model Adı"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Açıklama"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <Select
            label="Üst Kategori"
            value={formData.parentCategory}
            onChange={(value) => handleParentCategoryChange(value)}
            options={[
              { value: '', label: 'Seçiniz' },
              ...parentCategories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            required
          />
          <Select
            label="Alt Kategori"
            value={formData.category}
            onChange={(value) => setFormData({ ...formData, category: value })}
            options={[
              { value: '', label: 'Seçiniz' },
              ...availableSubcategories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            required
            disabled={!formData.parentCategory}
          />
          <Select
            label="Marka"
            value={formData.brand}
            onChange={(value) => setFormData({ ...formData, brand: value })}
            options={[
              { value: '', label: 'Seçiniz' },
              ...brands.map((brand) => ({
                value: brand.id,
                label: brand.name,
              })),
            ]}
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingModel ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ModelList;
